import type { ValidationError } from "./types";

const URL_PROTOCOL_PATTERN = /^[a-z][a-z\d+\-.]*:\/\//i;
const IPV4_PATTERN = /^\d{1,3}(?:\.\d{1,3}){3}$/;
const HOST_LABEL_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i;

export function normalizeUrl(url: string): string {
  const trimmed = url.trim();

  if (!trimmed || URL_PROTOCOL_PATTERN.test(trimmed)) {
    return trimmed;
  }

  return `https://${trimmed}`;
}

function isValidHostname(hostname: string): boolean {
  const normalizedHostname = hostname.endsWith(".")
    ? hostname.slice(0, -1)
    : hostname;

  if (!normalizedHostname || normalizedHostname.length > 253) {
    return false;
  }

  if (IPV4_PATTERN.test(normalizedHostname)) {
    return normalizedHostname
      .split(".")
      .every((part) => Number(part) >= 0 && Number(part) <= 255);
  }

  const labels = normalizedHostname.split(".");
  if (labels.length < 2) {
    return false;
  }

  return labels.every((label) => HOST_LABEL_PATTERN.test(label));
}

export function validateUrl(url: string): ValidationError | null {
  if (!url || !url.trim()) {
    return { field: "url", message: "Bitte geben Sie eine URL ein." };
  }

  const normalizedUrl = normalizeUrl(url);

  if (normalizedUrl.length > 2048) {
    return {
      field: "url",
      message: "Die URL darf maximal 2048 Zeichen lang sein.",
    };
  }

  try {
    const parsed = new URL(normalizedUrl);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return {
        field: "url",
        message: "Bitte geben Sie eine gueltige URL oder Domain ein.",
      };
    }

    if (!isValidHostname(parsed.hostname)) {
      return {
        field: "url",
        message: "Bitte geben Sie eine gueltige URL oder Domain ein.",
      };
    }
  } catch {
    return {
      field: "url",
      message: "Bitte geben Sie eine gueltige URL oder Domain ein.",
    };
  }

  return null;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function validateEmail(email: string): ValidationError | null {
  if (!email || !email.trim()) {
    return { field: "email", message: "Bitte geben Sie eine E-Mail-Adresse ein." };
  }

  if (email.length > 255) {
    return {
      field: "email",
      message: "Die E-Mail-Adresse darf maximal 255 Zeichen lang sein.",
    };
  }

  if (!EMAIL_REGEX.test(email)) {
    return {
      field: "email",
      message: "Bitte geben Sie eine gueltige E-Mail-Adresse ein.",
    };
  }

  return null;
}
